/* eslint-disable no-unused-vars */
/** @module Auth0PostLoginHandlerWebhook */
/* eslint-disable import/extensions */
import Auth0PostLoginHandlerBase from '../auth0-post-login-handler-base.mst.mjs';
import Auth0TokenStorageHelper from '../auth0-token-storage-helper.mjs';

/**
 * @class Auth0PostLoginHandlerWebhook
 * @description Webhook-style post-login handler that hands off control to an external URL.
 *
 *              This handler enables consuming applications to define their own post-login
 *              behavior without having to add custom handlers to the auth0-config repo.
 *
 *              When a `postLoginHandoffUrl` is provided during Auth0LoginHelper bootstrapping,
 *              this handler will POST the login data to that URL via a form submission,
 *              allowing the consuming app to handle post-login logic on their own server.
 *
 *              The handler includes the initial access token (and optionally refresh token)
 *              in the payload, so consuming apps don't need to use auth0-spa-js to acquire
 *              their first token.
 *
 * @example
 * // In consuming app's login page:
 * const loginHelper = new Auth0LoginHelper({
 *   domain: 'signin.shipstation.com',
 *   clientId: 'your-client-id',
 *   postLoginHandoffUrl: 'https://your-app.com/auth/post-login-handler',
 *   // ... other options
 * });
 *
 * // Your app's /auth/post-login-handler endpoint receives a POST with:
 * // - loginData: JSON string containing { user, appState, isAuthenticated, tokens }
 * //   where tokens = { accessToken: string, refreshToken?: string }
 */
class Auth0PostLoginHandlerWebhook extends Auth0PostLoginHandlerBase {
  constructor(options) {
    super(options);
    this._options = options;
    this._postLoginHandoffUrl = options.postLoginHandoffUrl;
    // Token storage config - if provided, tokens will be stored BEFORE posting to webhook
    // This allows apps to use both browser storage AND webhook handoff
    this._tokenStorageConfig = options.tokenStorageConfig;
  }

  /**
   * @inheritdoc
   * This handler should run if a postLoginHandoffUrl was provided.
   * It is registered first in the handler list so it takes priority.
   */
  async shouldHandle(loginData) {
    const hasHandoffUrl = !!this._postLoginHandoffUrl;
    if (hasHandoffUrl) {
      console.log(`Auth0PostLoginHandlerWebhook.shouldHandle() => true (handoff URL: ${this._postLoginHandoffUrl})`);
    }
    return Promise.resolve(hasHandoffUrl);
  }

  /**
   * @inheritdoc
   * Posts the login data to the handoff URL via form submission.
   * The form contains the loginData as a JSON string in a hidden field.
   * Includes the access token (and optionally refresh token) so consuming apps
   * can use them immediately without needing auth0-spa-js.
   *
   * If tokenStorageConfig is provided (non-empty array), tokens will ALSO be stored
   * in browser storage before posting to the webhook. This enables hybrid scenarios
   * where apps want both browser-accessible tokens AND server-side post-login handling.
   */
  async run(loginData) {
    console.log(`Auth0PostLoginHandlerWebhook.run() => handing off to ${this._postLoginHandoffUrl}`);

    // Store tokens in browser storage if config provided (allows hybrid cookie + webhook mode)
    if (loginData.tokens && this._tokenStorageConfig && this._tokenStorageConfig.length > 0) {
      console.log('Auth0PostLoginHandlerWebhook => storing tokens before webhook handoff:', JSON.stringify(this._tokenStorageConfig));
      Auth0TokenStorageHelper.storeTokens(loginData.tokens, this._tokenStorageConfig);
    } else if (loginData.tokens && (!this._tokenStorageConfig || this._tokenStorageConfig.length === 0)) {
      console.log('Auth0PostLoginHandlerWebhook => skipping token storage (no storage config or empty array)');
    }

    // Validate protocol before POSTing tokens — a javascript:/data: URL here would
    // exfiltrate tokens including refresh_token to an attacker-controlled destination.
    let safeHandoffUrl = null;
    try {
      const parsed = new URL(this._postLoginHandoffUrl);
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
        safeHandoffUrl = parsed.href;
      }
    } catch {
      // malformed URL — reject below
    }
    if (!safeHandoffUrl) {
      console.error(`Auth0PostLoginHandlerWebhook => postLoginHandoffUrl rejected (unsafe protocol): ${this._postLoginHandoffUrl}`);
      return;
    }

    // Create a form to POST the login data
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = safeHandoffUrl;
    form.style.display = 'none';

    // Build tokens object for the payload
    // Tokens are passed in from Auth0LoginHelper._fetchInitialTokens()
    const tokens = loginData.tokens
      ? {
        accessToken: loginData.tokens.accessToken,
        // Only include refresh token if it exists
        ...(loginData.tokens.refreshToken && { refreshToken: loginData.tokens.refreshToken }),
      }
      : undefined;

    if (tokens?.accessToken) {
      console.log(`Auth0PostLoginHandlerWebhook => including accessToken (${tokens.accessToken.substring(0, 20)}...)`);
    }
    if (tokens?.refreshToken) {
      console.log(`Auth0PostLoginHandlerWebhook => including refreshToken (${tokens.refreshToken.substring(0, 20)}...)`);
    }

    // Serialize login data to JSON, including tokens
    const loginDataJson = JSON.stringify({
      user: loginData.user,
      appState: loginData.appState,
      isAuthenticated: loginData.isAuthenticated,
      tokens,
    });

    // Add the login data as a hidden field
    const loginDataInput = document.createElement('input');
    loginDataInput.type = 'hidden';
    loginDataInput.name = 'loginData';
    loginDataInput.value = loginDataJson;
    form.appendChild(loginDataInput);

    // Add the form to the document and submit
    document.body.appendChild(form);
    form.submit();
  }
}

export default Auth0PostLoginHandlerWebhook;
